// useViewModalSave.js

import { useCallback } from 'react';

import { toast } from 'react-toastify';
import { isSystemTypeView } from 'services/views/TypeViewService';
import { createView, updateView } from 'services/views/ViewWriteService';
import { buildViewDefinitionDraft } from '../buildViewDefinitionDraft';

// Centralizes save behavior so ViewModal only wires inputs and actions.
const useViewModalSave = ({
    isEditMode,
    view,
    description,
    title,
    user,
    baseKey,
    createdFrom,
    dashboardKey,
    createPosition,
    type,
    typeKey,
    boardField,
    boardLayout,
    boardEnumOptions,
    viewGroupId,
    properties,
    filters,
    sorts,
    includeDescendants,
    timelineField,
    timelineRowMode,
    timelineGroupField,
    showUnscheduled,
    preset,
    showHierarchy,
    showDependencies,
    allowScheduleEdit,
    calendarField,
    chartConfig,
    scheduledType,
    scheduledField,
    slotIncrementMinutes,
    scheduledFilters,
    scheduledProperties,
    isGalleryDataType,
    filterFieldMap,
    scheduledFilterFieldMap,
    scheduledTemporalFieldMap,
    showProgress,
    hideProgress,
    onSave,
    setOpen,
}) => useCallback(async () => {
    if (!user?.key) return;
    if (!typeKey) return;
    const nextView = buildViewDefinitionDraft({
        description,
        title,
        type,
        boardField,
        boardLayout,
        boardEnumOptions,
        viewGroupId,
        properties,
        filters,
        sorts,
        includeDescendants,
        timelineField,
        timelineRowMode,
        timelineGroupField,
        showUnscheduled,
        preset,
        showHierarchy,
        showDependencies,
        allowScheduleEdit,
        calendarField,
        chartConfig,
        scheduledType,
        scheduledField,
        slotIncrementMinutes,
        scheduledFilters,
        scheduledProperties,
        isGalleryDataType,
        filterFieldMap,
        scheduledFilterFieldMap,
        scheduledTemporalFieldMap,
    });
    if (!nextView) return;

    try {
        if (isEditMode) {
            if (!view?.key) return;
            showProgress && showProgress('Updating view...');
            // System-type templates live under `_nodetypes/{type}/views`. Saving from
            // a base writes a `_views` override with the same key so the collection
            // merge can prefer the customized boardField / layout.
            const isSystemViewEdit = isSystemTypeView(view);
            const savedView = {
                ...nextView,
                key: view.key,
                nodeType: typeKey || null,
                scope: 'NODE_TYPE',
                baseKey: baseKey || view?.baseKey || null,
                userKey: user.key,
                ...(isSystemViewEdit ? {
                    isSystemView: false,
                    viewSource: 'BASE',
                    canDelete: true,
                } : {}),
            };
            await updateView(view.key, savedView);
            onSave && onSave({
                ...view,
                ...savedView,
            });
        } else {
            showProgress && showProgress('Creating view...');
            const created = await createView(
                user.key,
                nextView.type,
                nextView.title,
                {
                    description: nextView.description,
                    baseKey,
                    createdFrom,
                    dashboardKey,
                    position: createPosition,
                    nodeType: typeKey || null,
                    boardField: nextView.boardField,
                    boardLayout: nextView.boardLayout,
                    boardEnumOptions: nextView.boardEnumOptions,
                    viewGroupId: nextView.viewGroupId,
                    properties: nextView.properties || [],
                    filters: nextView.filters || [],
                    sorts: nextView.sorts || [],
                    includeDescendants: nextView.includeDescendants,
                    includeSubtitle: nextView.includeSubtitle,
                    includeCover: nextView.includeCover,
                    timelineField: nextView.timelineField,
                    timelineRowMode: nextView.timelineRowMode,
                    timelineGroupField: nextView.timelineGroupField,
                    showUnscheduled: nextView.showUnscheduled,
                    preset: nextView.preset,
                    showHierarchy: nextView.showHierarchy,
                    showDependencies: nextView.showDependencies,
                    allowScheduleEdit: nextView.allowScheduleEdit,
                    calendarField: nextView.calendarField,
                    chartConfig: nextView.chartConfig,
                    schedulerConfig: nextView.schedulerConfig,
                }
            );
            if (!created?.key) return;
            onSave && onSave(created);
        }

        setOpen(false);
    } catch (error) {
        console.error('[ViewModal] save view error:', error);
        toast.error(`View could not be saved: ${error?.message || 'Unknown error'}`);
    } finally {
        hideProgress && hideProgress();
    }
}, [
    isEditMode,
    view,
    description,
    title,
    user,
    baseKey,
    createdFrom,
    dashboardKey,
    createPosition,
    type,
    typeKey,
    boardField,
    boardLayout,
    boardEnumOptions,
    viewGroupId,
    properties,
    filters,
    sorts,
    includeDescendants,
    timelineField,
    timelineRowMode,
    timelineGroupField,
    showUnscheduled,
    preset,
    showHierarchy,
    showDependencies,
    allowScheduleEdit,
    calendarField,
    chartConfig,
    scheduledType,
    scheduledField,
    slotIncrementMinutes,
    scheduledFilters,
    scheduledProperties,
    isGalleryDataType,
    filterFieldMap,
    scheduledFilterFieldMap,
    scheduledTemporalFieldMap,
    showProgress,
    hideProgress,
    onSave,
    setOpen,
]);

export default useViewModalSave;
